/**
 * Who is in a comment thread, as the pin shows it: a stack of faces with a
 * "+N" tail.
 *
 * A pin used to carry ONE avatar — whoever opened the thread — so a thread
 * three people had argued in looked exactly like one nobody had answered.
 * The facepile is what makes an active thread legible before it is opened.
 *
 * PURE — no DOM, no Yjs, so the pin's geometry can be unit-tested and, more
 * importantly, so the hit-test and the renderer can compute the SAME width
 * from the same function. A pin whose painted tail is not clickable is worse
 * than no tail at all.
 */
import type { CommentMessage } from "@kola/shared";

/** One face in the stack. */
export interface ThreadFace {
  /** Author id — the identity the faces are de-duplicated by. */
  id: string;
  /** Denormalized display name at write time (what the initials come from). */
  name: string;
}

/** A thread's participants, capped for display. */
export interface ThreadFacepile {
  /** Faces to paint, thread author FIRST, in order of first message. */
  shown: ThreadFace[];
  /** How many further participants there are; 0 = everyone is shown. */
  overflow: number;
}

/** How many faces a pin shows before it starts counting instead. */
export const FACEPILE_LIMIT = 3;

/**
 * The thread's distinct participants, in order of first speaking, capped at
 * `limit` with the remainder counted.
 *
 * Ordered by FIRST message rather than most recent, so the lead face is the
 * person who opened the thread and stays put — a pile whose front face
 * changed every time someone replied would make the board flicker for a
 * reader who is not even in the conversation.
 *
 * De-duplicated by `authorId`: one person answering their own thread twice is
 * one face. A message with an empty author id (never written by this app, but
 * a doc is a doc) falls back to its name so it cannot collapse every such
 * message into a single face.
 */
export function threadFacepile(
  messages: readonly CommentMessage[],
  limit: number = FACEPILE_LIMIT,
): ThreadFacepile {
  const seen = new Set<string>();
  const faces: ThreadFace[] = [];
  for (const message of messages) {
    const id = message.authorId === "" ? `name:${message.authorName}` : message.authorId;
    if (seen.has(id)) continue;
    seen.add(id);
    faces.push({ id, name: message.authorName });
  }
  return {
    shown: faces.slice(0, limit),
    overflow: Math.max(0, faces.length - limit),
  };
}

/* ------------------------------------------------------------------ */
/* Geometry — shared by the renderer and the hit-test                   */
/* ------------------------------------------------------------------ */

/** How much of each following face the one in front of it covers (screen px). */
export const FACE_OVERLAP_PX = 9;

/** Width the "+N" tail adds (screen px) — must match `.comment-pin-more` CSS. */
export const FACEPILE_MORE_PX = 15;

/**
 * The pin's unscaled width for a given pile: the base teardrop, plus the
 * uncovered sliver of each extra face, plus the counter when there is one.
 *
 * `base` is PIN_SIZE_PX. It is a parameter rather than an import so this
 * module stays free of the component it sizes (which imports it back).
 */
export function pinWidthPx(facepile: ThreadFacepile, base: number): number {
  const extraFaces = Math.max(0, facepile.shown.length - 1);
  return base + extraFaces * FACE_OVERLAP_PX + (facepile.overflow > 0 ? FACEPILE_MORE_PX : 0);
}
